import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Bell, Check, Loader2, X } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { cn } from "@/lib/utils";

/**
 * Popover for assigning teammates to receive notifications about a debtor.
 * Only rendered for PRO owner/admin (see gating at call-site).
 */
const RecipientsPopover = ({ open, debtorId, anchorRect, onClose, onSaved }) => {
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const containerRef = useRef(null);

  // Close on ESC / outside click
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    const onDown = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, onClose]);

  // Load team + current recipients when opened
  useEffect(() => {
    if (!open || !debtorId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [membersRes, recipientsRes] = await Promise.all([
          apiClient.get("/team/members"),
          apiClient.get(`/debtors/${debtorId}/notification-recipients`),
        ]);
        if (cancelled) return;
        const all = [];
        if (membersRes.data?.owner) {
          all.push({ ...membersRes.data.owner, isOwner: true });
        }
        for (const m of membersRes.data?.members || []) {
          if (m.status === "active") all.push({ ...m, isOwner: false });
        }
        setMembers(all);
        setSelected(new Set(recipientsRes.data?.user_ids || []));
      } catch (e) {
        toast.error(formatApiError(e, "Couldn't load teammates."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, debtorId]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.patch(`/debtors/${debtorId}/notification-recipients`, {
        user_ids: Array.from(selected),
      });
      toast.success("Notification recipients updated.");
      onSaved?.(Array.from(selected));
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const style = useMemo(() => {
    if (!anchorRect) return { top: 100, left: 100 };
    const width = 340;
    const gap = 8;
    let left = anchorRect.right - width;
    if (left < 16) left = 16;
    return {
      top: anchorRect.bottom + gap + window.scrollY,
      left: left + window.scrollX,
      width,
    };
  }, [anchorRect]);

  if (!open) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.15 }}
        className="fixed z-[100]"
        style={style}
        data-testid="recipients-popover"
      >
        <div
          ref={containerRef}
          className="bg-bg-secondary border border-border-default rounded-[12px] shadow-elevated overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border-default">
            <div className="flex items-center gap-2">
              <Bell size={14} className="text-accent-pink" />
              <span className="text-white text-[13px] font-semibold">Notification Recipients</span>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-text-secondary hover:text-white p-1"
              data-testid="recipients-popover-close"
              aria-label="Close"
            >
              <X size={14} />
            </button>
          </div>

          <div className="max-h-[280px] overflow-y-auto p-2">
            {loading ? (
              <div className="flex items-center justify-center py-6 text-text-secondary text-[12px] gap-2">
                <Loader2 size={14} className="animate-spin" /> Loading team…
              </div>
            ) : members.length === 0 ? (
              <div className="px-3 py-4 text-text-secondary text-[12px]" data-testid="recipients-empty">
                No teammates yet. Invite one from Settings → Team.
              </div>
            ) : (
              <ul className="space-y-1" data-testid="recipients-list">
                {members.map((m) => {
                  const isSelected = selected.has(m.id);
                  return (
                    <li key={m.id}>
                      <button
                        type="button"
                        onClick={() => toggle(m.id)}
                        className={cn(
                          "w-full flex items-center gap-3 px-3 py-2 rounded-[8px] text-left transition-colors",
                          isSelected ? "bg-bg-tertiary" : "hover:bg-bg-tertiary/60"
                        )}
                        data-testid={`recipients-toggle-${m.id}`}
                      >
                        <span
                          className={cn(
                            "w-4 h-4 rounded-[4px] border flex items-center justify-center shrink-0",
                            isSelected ? "border-accent-pink bg-accent-pink" : "border-border-default"
                          )}
                        >
                          {isSelected && <Check size={11} className="text-[#091424]" strokeWidth={3} />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-white text-[13px] font-semibold truncate">
                            {m.full_name || m.email}
                          </span>
                          <span className="block text-text-secondary text-[11px] truncate">
                            {m.email}{m.isOwner ? " · Owner" : m.role ? ` · ${m.role}` : ""}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-border-default">
            <span className="text-text-secondary text-[11px]" data-testid="recipients-count">
              {selected.size} recipient{selected.size === 1 ? "" : "s"}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="h-8 px-3 rounded-[8px] border border-border-default text-text-secondary hover:text-white text-[12px] font-semibold"
                data-testid="recipients-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving || loading}
                className="h-8 px-3 rounded-[8px] bg-accent-pink text-[#091424] text-[12px] font-bold hover:bg-accent-pink-light transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                data-testid="recipients-save"
              >
                {saving && <Loader2 size={12} className="animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default RecipientsPopover;
